import { useFormik } from 'formik'
import { useSnackbar } from 'notistack'

import { Alert } from '@mui/material'
import { ConsoleDialog } from '@/components/ConsoleDialog'
import { MultiSelectBox } from '@/components/inputs/MultiSelectBox'
import {
  DistributionListSportKey,
  distributionListSportLabelsMap,
  possibleDistributionListSports
} from '@/constants/distributionList'
import { ApiError } from '@/service/ApiError'
import {
  addDistributionListRecipientSport,
  deleteDistributionListRecipientSport
} from '@/service/distributionListService'
import type { DistributionRecipientWithOperatorName } from '@/stores/distributionRecipients'
import { FormDialogProps } from '@/types/forms'
import { formikSaveButtonProps, onFieldChange } from '@/utils/forms/formik'

type Props = FormDialogProps<DistributionRecipientWithOperatorName[]>

type FormValues = {
  sports: DistributionListSportKey[]
}

export const DistributionRecipientsSportBulkEditForm = ({
  open,
  onSubmit,
  onClose,
  defaultValues
}: Props) => {
  const { enqueueSnackbar } = useSnackbar()

  const formik = useFormik<FormValues>({
    initialValues: {
      sports: []
    },

    onSubmit: async (values, { setSubmitting }) => {
      if (!values.sports.length || !defaultValues?.length) return

      try {
        setSubmitting(true)

        // We separate the add and delete operations to prevent potential race conditions —
        // ensuring all new sports are fully added for each recipient before any old sports are removed.
        // This avoids situations where a delete request could run before an add completes for the same recipient.
        await Promise.all(
          defaultValues
            .filter((recipient) => recipient.recipientType !== 'ADMIN')
            .map(async (recipient) => {
              const sportsToAdd = values.sports.filter((sport) => !recipient.sports.includes(sport))

              if (sportsToAdd.length > 0) {
                await Promise.all(
                  sportsToAdd.map((sport) =>
                    addDistributionListRecipientSport({
                      emailAddress: recipient.emailAddress,
                      sport
                    })
                  )
                )
              }
            })
        )

        await Promise.all(
          defaultValues
            .filter((recipient) => recipient.recipientType !== 'ADMIN')
            .map(async (recipient) => {
              const sportsToDelete = recipient.sports.filter(
                (sport) => !values.sports.includes(sport)
              )

              if (sportsToDelete.length > 0) {
                await Promise.all(
                  sportsToDelete.map((sport) =>
                    deleteDistributionListRecipientSport({
                      emailAddress: recipient.emailAddress,
                      sport
                    })
                  )
                )
              }
            })
        )

        onSubmit?.()
        enqueueSnackbar(`Successfully updated ${defaultValues.length} recipients' sports`, {
          variant: 'success'
        })
        setSubmitting(false)
        handleClose()
      } catch (e) {
        const errorMessage = [
          `Failed to update ${defaultValues.length} recipients`,
          (e as unknown as ApiError)?.message
        ]
          .filter(Boolean)
          .join(': ')
        enqueueSnackbar(errorMessage, { variant: 'error' })
        setSubmitting(false)
        // Throw the error again to be caught by dialog and doesn't close it
        throw errorMessage
      }
    }
  })

  const handleClose = () => {
    formik.resetForm()
    onClose?.()
  }

  return (
    <ConsoleDialog
      open={open}
      SaveButtonProps={formikSaveButtonProps(formik)}
      title="Edit Recipients"
      description="Select the sports below to edit distribution list recipients"
      onClose={handleClose}
    >
      {defaultValues?.some((recipient) => recipient.recipientType === 'ADMIN') && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          Some of the recipients are of type ADMIN and will not be affected by this change.
        </Alert>
      )}

      <Alert severity="error" sx={{ mb: 1 }}>
        All the previously assigned sports will be overwritten.
      </Alert>

      <MultiSelectBox
        label="Sports"
        options={possibleDistributionListSports}
        formatListOption={(option) => distributionListSportLabelsMap[option]}
        formatChipOption={(option) => distributionListSportLabelsMap[option]}
        name="sports"
        value={formik.values.sports}
        onChange={(value) => onFieldChange(formik, 'sports', value)}
        required
        fullWidth
        excludeSelected
        noOptionsMessage="No sports available"
      />
    </ConsoleDialog>
  )
}
